import { Fragment } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraMenu,
  ChakraMenuButton,
  ChakraMenuList,
  Divider,
  FAIconName,
  Icon,
  Text,
} from '@equip.health/ui';

import { OverviewPageEvents } from '~/analytics/overviewPageEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';

import PatientAlertBanner from '../tabs/overviews/PatientAlertBanner';
import { PatientAlert } from '~/lib/types/patient/PatientAlert';

const TOP_LEVEL_ID = 'patient-alerts';

type AlertsProps = {
  patientAlerts: PatientAlert[];
  patientId: string;
  onContinueSurvey: (externalId: string) => void;
  onAddNewSurvey: (surveyName: SurveyName) => void;
};

const PatientAlerts = ({
  patientAlerts,
  patientId,
  onContinueSurvey,
  onAddNewSurvey,
}: AlertsProps) => {
  const { trackEvent } = useAnalytics();

  const hasAlerts = patientAlerts.length > 0;

  const handleAlertButtonClick = () => {
    trackEvent(OverviewPageEvents.alertButtonOverviewTabClicked, {
      Source: AnalyticsSourceValues.PATIENT_HEADER,
    });
  };

  return (
    <ChakraMenu closeOnSelect id={`${TOP_LEVEL_ID}__menu`}>
      {({ onClose }) => (
        <>
          <ChakraMenuButton
            background="transparent"
            border="none"
            id={`${TOP_LEVEL_ID}__menu-button`}
            minWidth="unset"
            onClick={handleAlertButtonClick}
            padding="4px"
            aria-label="Alerts"
          >
            <ChakraBox
              alignItems="center"
              backgroundColor={
                hasAlerts ? 'critical.background' : 'transparent'
              }
              borderRadius="50%"
              display="inline-flex"
              justifyContent="center"
              padding="10px"
              position="relative"
            >
              <Icon
                color={hasAlerts ? 'critical.base' : 'primary.100'}
                name={FAIconName.bellRegular}
                size="lg"
              />
              {hasAlerts && (
                <ChakraBox
                  alignItems="center"
                  backgroundColor="critical.base"
                  borderRadius="50%"
                  color="white"
                  display="flex"
                  fontSize="10px"
                  fontWeight="bold"
                  height="18px"
                  id={`${TOP_LEVEL_ID}__alert-count`}
                  justifyContent="center"
                  minWidth="18px"
                  position="absolute"
                  right="-4px"
                  top="-4px"
                >
                  {patientAlerts.length}
                </ChakraBox>
              )}
            </ChakraBox>
          </ChakraMenuButton>

          {hasAlerts && (
            <ChakraMenuList
              borderColor="neutral.line"
              borderRadius="12px"
              boxShadow="0px 0px 8px 0px rgba(0, 0, 0, 0.16)"
              gap="18px"
              id={`${TOP_LEVEL_ID}__menu-list`}
              maxWidth="480px"
              padding="32px"
              zIndex={100}
            >
              <ChakraFlex flexDirection="column" gap="24px">
                <ChakraFlex gap="6px">
                  <Icon
                    color="critical.base"
                    name={FAIconName.exclamationTriangleRegular}
                    size="lg"
                  />
                  <Text color="neutral.primary" variant="h5">
                    Alerts
                  </Text>
                </ChakraFlex>
                {patientAlerts.map((patientAlert, index) => (
                  <Fragment key={`${TOP_LEVEL_ID}__survey-banner-${index}`}>
                    <PatientAlertBanner
                      patientAlert={patientAlert}
                      id={`${TOP_LEVEL_ID}__banner`}
                      key={`${patientAlert.name}-${patientAlert.updatedDate}`}
                      onClickAddNew={onAddNewSurvey}
                      onClose={onClose}
                      onContinueSurvey={onContinueSurvey}
                      patientId={patientId}
                    />
                    {patientAlerts.length - 1 > index && (
                      <Divider id={`${TOP_LEVEL_ID}__divider-${index}`} />
                    )}
                  </Fragment>
                ))}
              </ChakraFlex>
            </ChakraMenuList>
          )}
        </>
      )}
    </ChakraMenu>
  );
};

export default PatientAlerts;
